import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, Loader2, ArrowUp, ArrowDown } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";

const inp = "border-zinc-800 bg-zinc-950 text-[13px] text-zinc-100";
const Field = ({ label, children }) => <label className="block"><span className="mb-1 block text-[12px] text-zinc-500">{label}</span>{children}</label>;
const move = (arr, i, d) => { const a = [...arr]; const j = i + d; if (j < 0 || j >= a.length) return a; [a[i], a[j]] = [a[j], a[i]]; return a; };

function ListEditor({ title, items, setItems, blank, render, testid }) {
  return (
    <section className="rounded-lg border border-zinc-800 p-4" data-testid={testid}>
      <div className="mb-3 flex items-center justify-between">
        <h4 className="text-[14px] font-semibold text-zinc-100">{title} ({items.length})</h4>
        <button data-testid={`${testid}-add`} onClick={() => setItems([...items, blank()])} className="inline-flex items-center gap-1 rounded-md border border-zinc-700 px-2.5 py-1 text-[12px] text-zinc-300 hover:bg-zinc-800"><Plus size={13} /> Új</button>
      </div>
      <div className="space-y-3">
        {items.map((it, i) => (
          <div key={it.id || i} className="rounded-md border border-zinc-800/80 bg-zinc-950/50 p-3">
            <div className="mb-2 flex justify-end gap-1 text-zinc-500">
              <button onClick={() => setItems(move(items, i, -1))} className="hover:text-zinc-200"><ArrowUp size={14} /></button>
              <button onClick={() => setItems(move(items, i, 1))} className="hover:text-zinc-200"><ArrowDown size={14} /></button>
              <button data-testid={`${testid}-del-${i}`} onClick={() => setItems(items.filter((_, j) => j !== i))} className="hover:text-red-400"><Trash2 size={14} /></button>
            </div>
            {render(it, (patch) => setItems(items.map((x, j) => (j === i ? { ...x, ...patch } : x))), i)}
          </div>
        ))}
      </div>
    </section>
  );
}

function QuestionEdit({ q, set, i }) {
  return (
    <div className="space-y-2">
      <Textarea data-testid={`q-text-${i}`} value={q.text} onChange={(e) => set({ text: e.target.value })} placeholder="Kérdés" className={`${inp} min-h-[56px]`} />
      {q.options.map((o, k) => (
        <div key={k} className="flex items-center gap-2">
          <input type="radio" checked={q.correct === k} onChange={() => set({ correct: k })} data-testid={`q-${i}-correct-${k}`} className="accent-emerald-500" title="Helyes válasz" />
          <Input value={o} onChange={(e) => set({ options: q.options.map((x, j) => (j === k ? e.target.value : x)) })} className={`${inp} h-8`} />
          {q.options.length > 2 && <button onClick={() => set({ options: q.options.filter((_, j) => j !== k), correct: k < q.correct ? q.correct - 1 : k === q.correct ? 0 : q.correct })} className="text-zinc-500 hover:text-red-400"><Trash2 size={13} /></button>}
        </div>
      ))}
      {q.options.length < 6 && <button onClick={() => set({ options: [...q.options, ""] })} className="text-[12px] text-blue-400">+ opció</button>}
      <Textarea value={q.explanation} onChange={(e) => set({ explanation: e.target.value })} placeholder="Magyarázat (a válasz után jelenik meg)" className={`${inp} min-h-[48px]`} />
    </div>
  );
}

function CaseEdit({ c, set }) {
  return (
    <div className="space-y-2">
      <div className="grid gap-2 sm:grid-cols-2">
        <Input value={c.title} onChange={(e) => set({ title: e.target.value })} placeholder="Cím" className={`${inp} h-8`} />
        <Input value={c.platform} onChange={(e) => set({ platform: e.target.value })} placeholder="Platform" className={`${inp} h-8`} />
      </div>
      <Textarea value={c.story} onChange={(e) => set({ story: e.target.value })} placeholder="Történet (markdown)" className={`${inp} min-h-[70px]`} />
      <Textarea value={c.tasks.join("\n")} onChange={(e) => set({ tasks: e.target.value.split("\n") })} placeholder="Feladatok (soronként egy)" className={`${inp} min-h-[70px]`} />
      <div className="text-[12px] text-zinc-500">Értékelési szempontok (pont + mintaválasz)</div>
      {c.rubric.map((r, k) => (
        <div key={k} className="grid gap-2 sm:grid-cols-[1fr_70px_2fr_auto]">
          <Input value={r.label} onChange={(e) => set({ rubric: c.rubric.map((x, j) => (j === k ? { ...x, label: e.target.value } : x)) })} className={`${inp} h-8`} />
          <Input type="number" min={0} max={10} value={r.points} onChange={(e) => set({ rubric: c.rubric.map((x, j) => (j === k ? { ...x, points: Number(e.target.value) } : x)) })} className={`${inp} h-8`} />
          <Textarea value={r.model} onChange={(e) => set({ rubric: c.rubric.map((x, j) => (j === k ? { ...x, model: e.target.value } : x)) })} className={`${inp} min-h-[32px]`} />
          <button onClick={() => set({ rubric: c.rubric.filter((_, j) => j !== k) })} className="text-zinc-500 hover:text-red-400"><Trash2 size={13} /></button>
        </div>
      ))}
      <button onClick={() => set({ rubric: [...c.rubric, { label: "", points: 1, model: "" }] })} className="text-[12px] text-blue-400">+ szempont</button>
    </div>
  );
}

function SimJson({ m, s }) {
  const [txt, setTxt] = useState(() => JSON.stringify(m.tickets || [], null, 1));
  const [err, setErr] = useState("");
  const change = (v) => {
    setTxt(v);
    try { s({ tickets: JSON.parse(v) }); setErr(""); } catch { setErr("Érvénytelen JSON – a mentés az utolsó érvényes állapotot küldi."); }
  };
  return (
    <section className="space-y-3 rounded-lg border border-zinc-800 p-4" data-testid="sim-editor">
      <Field label="Döntési opciók (soronként egy)"><Textarea value={(m.decisions || []).join("\n")} onChange={(e) => s({ decisions: e.target.value.split("\n").filter((x) => x.trim()) })} className={`${inp} min-h-[120px]`} /></Field>
      <Field label="Bejelentések (JSON: reporter_name, reporter_email, platform, url, category, description, evidence[], minutes_ago, expected{...}, explanation)">
        <Textarea data-testid="sim-json" value={txt} onChange={(e) => change(e.target.value)} className={`${inp} min-h-[420px] font-mono text-[11px]`} />
      </Field>
      {err && <p className="text-[12px] text-red-400">{err}</p>}
      <p className="text-[12px] text-zinc-500">Az expected mezőkben a triage checklist opcióinak pontos szövege szerepeljen (több elfogadott érték listában). A decision értéke egyezzen egy döntési opcióval.</p>
    </section>
  );
}

function ModuleForm({ m, setM }) {
  const s = (patch) => setM({ ...m, ...patch });
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Field label="Rövid cím"><Input data-testid="mod-title" value={m.title} onChange={(e) => s({ title: e.target.value })} className={inp} /></Field>
        <Field label="Teljes cím"><Input value={m.full_title || ""} onChange={(e) => s({ full_title: e.target.value })} className={inp} /></Field>
        <Field label="Alcím / téma"><Input value={m.subtitle || ""} onChange={(e) => s({ subtitle: e.target.value })} className={inp} /></Field>
        <Field label="Jelvény"><Input value={m.badge} onChange={(e) => s({ badge: e.target.value })} className={inp} /></Field>
        <Field label="Szint cím"><Input value={m.level_title} onChange={(e) => s({ level_title: e.target.value })} className={inp} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Idő (perc)"><Input type="number" value={m.minutes} onChange={(e) => s({ minutes: Number(e.target.value) })} className={inp} /></Field>
          <Field label="Átmenő %"><Input data-testid="mod-pass-pct" type="number" value={m.pass_pct} onChange={(e) => s({ pass_pct: Number(e.target.value) })} className={inp} /></Field>
        </div>
      </div>
      <label className="flex items-center gap-2 text-[13px] text-zinc-300"><Switch checked={!!m.emergency} onCheckedChange={(v) => s({ emergency: v })} /> „Call 112” vészhelyzeti sáv megjelenítése</label>
      <Field label="Tanulási célok (soronként egy)"><Textarea value={(m.objectives || []).join("\n")} onChange={(e) => s({ objectives: e.target.value.split("\n") })} className={`${inp} min-h-[70px]`} /></Field>
      {m.kind !== "module" && <Field label="Bevezető (markdown)"><Textarea value={m.intro || ""} onChange={(e) => s({ intro: e.target.value })} className={`${inp} min-h-[70px]`} /></Field>}
      {m.kind === "module" && (
        <>
          <ListEditor title="Leckék" testid="lessons-editor" items={m.lessons} setItems={(lessons) => s({ lessons })} blank={() => ({ title: "Új lecke", body: "", playbook: false })}
            render={(l, set, i) => (
              <div className="space-y-2">
                <Input data-testid={`lesson-title-${i}`} value={l.title} onChange={(e) => set({ title: e.target.value })} className={`${inp} h-8`} />
                <Textarea data-testid={`lesson-body-${i}`} value={l.body} onChange={(e) => set({ body: e.target.value })} placeholder="Tartalom (markdown: **félkövér**, - lista, | táblázat |)" className={`${inp} min-h-[160px] font-mono text-[12px]`} />
                <label className="flex items-center gap-2 text-[12px] text-zinc-400"><Switch checked={!!l.playbook} onCheckedChange={(v) => set({ playbook: v })} /> Platform playbook („Last reviewed” címke)</label>
              </div>
            )} />
          <section className="rounded-lg border border-zinc-800 p-4">
            <h4 className="mb-3 text-[14px] font-semibold text-zinc-100">Mini-küldetés</h4>
            <div className="grid gap-2 sm:grid-cols-[1fr_100px]">
              <Input value={m.mission?.title || ""} onChange={(e) => s({ mission: { ...(m.mission || { minutes: 10, body: "" }), title: e.target.value } })} placeholder="Cím" className={`${inp} h-8`} />
              <Input type="number" value={m.mission?.minutes || 10} onChange={(e) => s({ mission: { ...(m.mission || { title: "", body: "" }), minutes: Number(e.target.value) } })} className={`${inp} h-8`} />
            </div>
            <Textarea value={m.mission?.body || ""} onChange={(e) => s({ mission: { ...(m.mission || { title: "", minutes: 10 }), body: e.target.value } })} placeholder="Leírás" className={`${inp} mt-2 min-h-[70px]`} />
          </section>
        </>
      )}
      {m.kind === "simulation" ? <SimJson m={m} s={s} /> : m.kind === "capstone" ? (
        <ListEditor title="Esettanulmányok" testid="cases-editor" items={m.cases || []} setItems={(cases) => s({ cases })} blank={() => ({ title: "", platform: "", story: "", tasks: [], rubric: [] })}
          render={(c, set) => <CaseEdit c={c} set={set} />} />
      ) : (
        <ListEditor title="Kvízkérdések" testid="questions-editor" items={m.questions} setItems={(questions) => s({ questions })} blank={() => ({ text: "", options: ["", "", "", ""], correct: 0, explanation: "" })}
          render={(q, set, i) => <QuestionEdit q={q} set={set} i={i} />} />
      )}
    </div>
  );
}

export function ModAcademyBuilder() {
  const [mods, setMods] = useState(null);
  const [sel, setSel] = useState("m1");
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/academy/admin/modules").then((r) => setMods(r.data)); }, []);
  useEffect(() => { if (mods) setDraft(JSON.parse(JSON.stringify(mods.find((m) => m.id === sel)))); }, [mods, sel]);
  const save = async () => {
    setBusy(true);
    try {
      const clean = { ...draft, objectives: draft.objectives.filter((o) => o.trim()), cases: (draft.cases || []).map((c) => ({ ...c, tasks: c.tasks.filter((t) => t.trim()) })) };
      const r = await api.put(`/academy/admin/modules/${sel}`, clean);
      setMods(mods.map((m) => (m.id === sel ? r.data : m)));
      toast.success("Modul mentve");
    } catch (e) { toast.error(apiErr(e?.response?.data?.detail)); } finally { setBusy(false); }
  };
  if (!mods || !draft) return <Loader2 className="animate-spin text-zinc-500" />;
  return (
    <div className="grid gap-5 lg:grid-cols-[220px_1fr]" data-testid="academy-builder">
      <div className="space-y-1">
        {mods.map((m) => (
          <button key={m.id} data-testid={`builder-module-${m.id}`} onClick={() => setSel(m.id)}
            className={`w-full rounded-md px-3 py-2 text-left text-[13px] ${sel === m.id ? "bg-zinc-800 text-zinc-50" : "text-zinc-400 hover:bg-zinc-900"}`}>
            <span className="mr-2 text-zinc-500">{m.kind === "capstone" ? "C" : m.kind === "final" ? "F" : m.kind === "simulation" ? "S" : m.order}</span>{m.title}
          </button>
        ))}
      </div>
      <div className="min-w-0 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-zinc-50">{draft.full_title || draft.title}</h3>
          <button data-testid="builder-save" onClick={save} disabled={busy} className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-[13px] font-semibold text-white hover:bg-blue-500 disabled:opacity-50">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Mentés
          </button>
        </div>
        <ModuleForm m={draft} setM={setDraft} />
      </div>
    </div>
  );
}
